import React, { useState, useMemo, useContext, useCallback, useEffect } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Stack,
  TextField,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { debounce } from 'lodash';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import { neutral } from '_lex/_foundations/phxColors';
import EisTemplatePicker from './EisTemplatePicker';
import type { CreateEisRuleRequest, EisRuleListItem, UpdateEisRuleRequest } from '../_types';
import type { CompactTemplate } from '../../Templates/_shared/_types';
import { useTemplates } from '../../Templates/_shared/_data_sources/useTemplates';

interface RuleFormData {
  name: string;
  templateId: number | null;
  description: string;
  ruleType: 'Frequency' | 'Scoring' | '';
}

const createRuleSchema = Yup.object()
  .shape({
    name: Yup.string().required(t('performanceReporting.eis.create.form.ruleName.required')),
    templateId: Yup.number()
      .nullable()
      .required(t('performanceReporting.eis.create.form.template.required')),
    description: Yup.string().default(''),
    ruleType: Yup.string()
      .oneOf(['Frequency', 'Scoring'])
      .required(t('performanceReporting.eis.create.form.ruleType.required')),
  })
  .defined() as Yup.ObjectSchema<RuleFormData>;

const editRuleSchema = Yup.object()
  .shape({
    name: Yup.string().required(t('performanceReporting.eis.create.form.ruleName.required')),
    templateId: Yup.number()
      .nullable()
      .required(t('performanceReporting.eis.create.form.template.required')),
    description: Yup.string().default(''),
    ruleType: Yup.string()
      .oneOf(['Frequency', 'Scoring'], t('performanceReporting.eis.create.form.ruleType.required'))
      .required(t('performanceReporting.eis.create.form.ruleType.required')),
  })
  .defined() as Yup.ObjectSchema<RuleFormData>;

interface RuleFormProps {
  onCreate?: (data: CreateEisRuleRequest) => Promise<void>;
  onSave?: (data: UpdateEisRuleRequest) => Promise<void>;
  onCancel: () => void;
  serverErrors?: ServerErrorResponse;
  setServerErrors: (errors: ServerErrorResponse | undefined) => void;
  initialValues?: EisRuleListItem;
  isEditMode?: boolean;
}

const getDefaultValues = (initialValues?: EisRuleListItem | null, isEditMode?: boolean): RuleFormData => {
  if (isEditMode && initialValues) {
    return {
      name: initialValues.name || '',
      templateId: initialValues.template?.id ?? null,
      description: initialValues.description ?? '',
      ruleType: initialValues.ruleType ?? '',
    };
  }
  return {
    name: '',
    templateId: null,
    description: '',
    ruleType: '',
  };
};

const RuleForm: React.FC<RuleFormProps> = ({
  onCreate,
  onSave,
  onCancel,
  serverErrors,
  setServerErrors,
  initialValues,
  isEditMode = false,
}) => {
  const { currentOrganizationId } = useContext(AppContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');

  const debouncedSetTemplateSearch = useMemo(() => debounce(setTemplateSearch, 300), []);

  const handleTemplateSearchChange = useCallback(
    (value: string) => {
      if (value) {
        debouncedSetTemplateSearch(value);
      } else {
        debouncedSetTemplateSearch.cancel();
        setTemplateSearch('');
      }
    },
    [debouncedSetTemplateSearch],
  );

  const templateParams = useMemo(
    () => ({
      ...(currentOrganizationId ? { organizationId: currentOrganizationId } : {}),
      state: 'ACTIVE',
      isCompactResponse: true,
      ...(templateSearch ? { search: templateSearch } : {}),
    }),
    [currentOrganizationId, templateSearch],
  );
  const templatesQuery = useTemplates(templateParams);
  const allTemplates = templatesQuery.data?.pages.flatMap((page) => page.content as unknown as CompactTemplate[]) || [];
  const templateOptions = useMemo(
    () => allTemplates.map((template) => ({ id: template.templateId, name: template.templateName })),
    [allTemplates],
  );

  const handleTemplateScrollToBottom = useCallback(() => {
    if (templatesQuery.hasNextPage && !templatesQuery.isFetchingNextPage) {
      templatesQuery.fetchNextPage();
    }
  }, [templatesQuery.hasNextPage, templatesQuery.isFetchingNextPage, templatesQuery.fetchNextPage]);

  const defaultValues = useMemo(() => getDefaultValues(initialValues, isEditMode), [initialValues?.id, isEditMode]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    reset,
  } = useForm<RuleFormData>({
    defaultValues,
    resolver: yupResolver(isEditMode ? editRuleSchema : createRuleSchema),
    mode: 'onSubmit',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const onSubmit = async (data: RuleFormData) => {
    setServerErrors(undefined);
    const isValid = await trigger();
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      if (isEditMode && onSave) {
        const request: UpdateEisRuleRequest = {
          name: data.name,
          description: data.description || undefined,
          templateId: data.templateId!,
          organizationId: currentOrganizationId || '',
          ruleType: data.ruleType as 'Frequency' | 'Scoring',
        };
        await onSave(request);
      } else if (onCreate) {
        const request: CreateEisRuleRequest = {
          name: data.name,
          description: data.description || undefined,
          templateId: data.templateId!,
          organizationId: currentOrganizationId || '',
          ruleType: data.ruleType as 'Frequency' | 'Scoring',
        };
        await onCreate(request);
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && ('errorDetails' in error || 'errorMessage' in error)) {
        setServerErrors(error as ServerErrorResponse);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Card
        sx={{
          border: '1px solid',
          borderColor: neutral[200],
          boxShadow: 'none',
        }}
        className='lex-form'
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          autoComplete='off'
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant='h5'
              sx={{ mb: 3, fontWeight: 600 }}
            >
              {isEditMode
                ? t('performanceReporting.eis.edit.pageTitle')
                : t('performanceReporting.eis.create.pageTitle')}
            </Typography>

            {errorHelper.renderAllErrors(serverErrors)}

            <Stack spacing={3}>
              <Controller
                name='name'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={`${t('performanceReporting.eis.create.form.ruleName.label')} *`}
                    fullWidth
                    error={!!errors.name}
                    helperText={(errors.name?.message as string) || ''}
                    disabled={isSubmitting}
                  />
                )}
              />

              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('performanceReporting.eis.create.form.description.label')}
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={(errors.description?.message as string) || ''}
                    disabled={isSubmitting}
                  />
                )}
              />

              <Controller
                name='templateId'
                control={control}
                render={({ field }) => (
                  <Box>
                    <EisTemplatePicker
                      id='rule-form-template'
                      label={`${t('performanceReporting.eis.create.form.template.label')} *`}
                      placeholder={t('performanceReporting.eis.create.form.template.placeholder')}
                      value={field.value}
                      onChange={field.onChange}
                      options={templateOptions}
                      disabled={isSubmitting}
                      error={!!errors.templateId}
                      helperText={(errors.templateId?.message as string) || ''}
                      isLoading={templatesQuery.isLoading || templatesQuery.isFetchingNextPage}
                      onScrollToBottom={handleTemplateScrollToBottom}
                      onSearchChange={handleTemplateSearchChange}
                    />
                  </Box>
                )}
              />

              <>
                <Divider sx={{ mt: 3, mb: 4 }} />
                <Controller
                  name='ruleType'
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      error={!!errors.ruleType}
                      component='fieldset'
                    >
                      <RadioGroup
                        {...field}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        sx={{ gap: 1 }}
                      >
                        <FormControlLabel
                          value='Frequency'
                          control={<Radio disabled={isEditMode} />}
                          sx={{ mt: 0, alignItems: 'flex-start' }}
                          label={
                            <Box>
                              <Typography
                                variant='body1'
                                fontWeight={600}
                              >
                                {t('performanceReporting.eis.create.form.ruleType.frequency')}
                              </Typography>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                {t('performanceReporting.eis.create.form.ruleType.frequencySubtitle')}
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value='Scoring'
                          control={<Radio disabled={isEditMode} />}
                          sx={{ mt: 0, alignItems: 'flex-start' }}
                          label={
                            <Box>
                              <Typography
                                variant='body1'
                                fontWeight={600}
                              >
                                {t('performanceReporting.eis.create.form.ruleType.scoring')}
                              </Typography>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                {t('performanceReporting.eis.create.form.ruleType.scoringSubtitle')}
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                      {errors.ruleType?.message && (
                        <FormHelperText>{errors.ruleType.message as string}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </>
            </Stack>
          </CardContent>

          <CardActions sx={{ p: 4, pt: 0 }}>
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 2 }}>
              <Button
                variant='outlined'
                color='neutral'
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {isEditMode
                  ? t('performanceReporting.eis.edit.buttons.cancel')
                  : t('performanceReporting.eis.create.buttons.cancel')}
              </Button>
              <Button
                variant='contained'
                color='primary'
                type='submit'
                disabled={isSubmitting}
                loading={isSubmitting}
                loadingPosition='end'
              >
                {isEditMode
                  ? t('performanceReporting.eis.edit.buttons.save')
                  : t('performanceReporting.eis.create.buttons.create')}
              </Button>
            </Box>
          </CardActions>
        </form>
      </Card>
    </Box>
  );
};

export default RuleForm;
